import { useState, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { supabase } from '../../lib/supabase';
import { SITE_ID, nowTR } from '../../lib/constants';
import { useThemeContext } from '../../contexts/ThemeContext';

export default function ContactPage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [telefon, setTelefon] = useState('');
  const [telefonError, setTelefonError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const { theme } = useThemeContext();
  const { contact } = theme;

  const formatTelefon = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
    if (digits.length > 6) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return digits;
  };

  const handleTelefonChange = (val: string) => { setTelefon(formatTelefon(val)); setTelefonError(''); };

  const validateTelefon = (val: string): boolean => {
    if (!val.trim()) return true;
    const digits = val.replace(/\D/g, '');
    if (digits.length !== 10) { setTelefonError('Telefon 10 haneli olmalıdır'); return false; }
    if (!digits.startsWith('5')) { setTelefonError('Telefon numarası 5 ile başlamalıdır'); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const mesaj = (form.elements.namedItem('mesaj') as HTMLTextAreaElement).value;
    if (mesaj.length > 500) return;
    if (!validateTelefon(telefon)) return;
    setSubmitting(true); setSubmitError('');
    const ad_soyad = (form.elements.namedItem('ad_soyad') as HTMLInputElement).value;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const konu = (form.elements.namedItem('konu') as HTMLSelectElement).value;
    try {
      const { error } = await supabase.from('iletisim_mesajlari').insert({
        site_id: SITE_ID, ad_soyad, email,
        telefon: telefon ? `+90 ${telefon}` : null,
        konu, mesaj, okundu: false, olusturma_tarihi: nowTR(),
      });
      if (error) throw error;
      const formData = new URLSearchParams();
      formData.append('ad_soyad', ad_soyad); formData.append('email', email);
      formData.append('telefon', telefon ? `+90 ${telefon}` : '');
      formData.append('konu', konu); formData.append('mesaj', mesaj);
      await fetch('https://readdy.ai/api/form/d6tb8tfcrcujsk0v16d0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() });
      setSubmitted(true); form.reset(); setTelefon(''); setCharCount(0);
    } catch { setSubmitError('Mesaj gönderilemedi. Lütfen tekrar deneyin.'); }
    finally { setSubmitting(false); }
  };

  const infoItems = [
    { icon: 'ri-map-pin-line', title: 'Adresimiz', content: contact.address },
    { icon: 'ri-phone-line', title: 'Telefon', content: `${contact.phone}\n${contact.working_hours.split('\n')[0]}` },
    { icon: 'ri-mail-line', title: 'E-posta', content: contact.email },
    { icon: 'ri-time-line', title: 'Çalışma Saatleri', content: contact.working_hours },
  ];

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />
      <main className="pt-24 pb-20">
        <div className="bg-coffee py-16 mb-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center">
            <p className="text-gold font-medium text-sm tracking-widest uppercase mb-3">Bize Ulaşın</p>
            <h1 className="font-serif text-4xl font-bold text-white mb-4">İletişim</h1>
            <p className="text-cream/70 max-w-lg mx-auto">Sorularınız, önerileriniz veya sipariş bilgisi için bize ulaşın.</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-4">
              {infoItems.map(item => (
                <div key={item.title} className="bg-white rounded-2xl p-5 flex gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-gold/10 rounded-xl"><i className={`${item.icon} text-gold text-lg`} /></div>
                  <div><p className="font-semibold text-coffee text-sm mb-1">{item.title}</p><p className="text-xs text-coffee-light whitespace-pre-line leading-relaxed">{item.content}</p></div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-2 bg-white rounded-2xl p-8">
              <h2 className="font-serif text-2xl font-semibold text-coffee mb-6">Mesaj Gönder</h2>
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4"><i className="ri-checkbox-circle-fill text-green-500 text-3xl" /></div>
                  <h3 className="font-serif text-xl font-semibold text-coffee mb-2">Mesajınız Alındı!</h3>
                  <p className="text-coffee-light mb-6">En kısa sürede size dönüş yapacağız.</p>
                  <button onClick={() => setSubmitted(false)} className="px-6 py-2.5 bg-coffee text-white rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap text-sm">Yeni Mesaj Gönder</button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form id="iletisim-formu" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><label className="block text-xs font-medium text-coffee-medium mb-1.5">Ad Soyad *</label><input type="text" name="ad_soyad" required placeholder="Adınız Soyadınız" className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee" /></div>
                    <div><label className="block text-xs font-medium text-coffee-medium mb-1.5">E-posta *</label><input type="email" name="email" required placeholder="ornek@mail.com" className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee" /></div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-coffee-medium mb-1.5">Telefon</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-coffee-medium select-none pointer-events-none">+90</span>
                      <input type="tel" name="telefon" value={telefon} onChange={e => handleTelefonChange(e.target.value)} onBlur={() => validateTelefon(telefon)} placeholder="5XX XXX XX XX" maxLength={13} className={`w-full pl-12 pr-4 py-3 text-sm bg-cream rounded-xl border ${telefonError ? 'border-red-400' : 'border-transparent'} focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee`} />
                    </div>
                    {telefonError && <p className="text-xs text-red-500 mt-1">{telefonError}</p>}
                  </div>
                  <div><label className="block text-xs font-medium text-coffee-medium mb-1.5">Konu *</label><select name="konu" required className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee cursor-pointer"><option value="">Konu seçin</option><option>Sipariş Hakkında</option><option>Ürün Bilgisi</option><option>İade / Değişim</option><option>Kargo Takip</option><option>Diğer</option></select></div>
                  <div>
                    <label className="block text-xs font-medium text-coffee-medium mb-1.5">Mesaj * <span className={`ml-auto float-right ${charCount > 480 ? 'text-red-500' : 'text-coffee-light'}`}>{charCount}/500</span></label>
                    <textarea name="mesaj" required rows={5} maxLength={500} onChange={e => setCharCount(e.target.value.length)} placeholder="Mesajınızı buraya yazın..." className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee resize-none" />
                  </div>
                  {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{submitError}</div>}
                  <button type="submit" disabled={submitting} className="w-full py-3.5 bg-coffee hover:bg-coffee-medium text-white font-semibold rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap">
                    {submitting ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" /> Gönderiliyor...</span> : <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-line" /> Mesaj Gönder</span>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
